'use client';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { FaqSection } from '@/components/sections/faq-section';
import { HelpdeskHero } from '@/components/sections/helpdesk-hero';
import Logos from '@/components/sections/logos';
import Testimonials from '@/components/sections/testimonials';
import { LinkPreview } from '@/components/ui/link-preview';
import { useTranslation } from '@/lib/i18n/context';

const GapDetectionCapabilities = () => {
  const { t } = useTranslation();

  const features = [
    {
      id: 1,
      title: t.features_pages['Handoff Pattern Analysis'],
      description:
        t.features_pages[
          'Track which knowledge sources cause the most AI-to-human handoffs. Bad knowledge reveals itself through escalations.'
        ],
    },
    {
      id: 2,
      title: t.features_pages['Cross-Channel Gap Clustering'],
      description:
        t.features_pages[
          "See knowledge gaps across Voice, Chat, Email, WhatsApp, Social, and Slack. Identify what's missing everywhere."
        ],
    },
    {
      id: 3,
      title: t.features_pages['Automatic Prioritization'],
      description:
        t.features_pages[
          'Gaps ranked by impact. Fix the documentation that causes the most handoffs first.'
        ],
    },
    {
      id: 4,
      title: t.features_pages['Suggested Improvements'],
      description:
        t.features_pages[
          'AI suggests what to add to your knowledge base. Not just identifying gaps—showing you how to fill them.'
        ],
    },
    {
      id: 5,
      title: t.features_pages['Knowledge Base Autopilot'],
      description:
        t.features_pages[
          'Your knowledge base maintains itself. Agent 5 identifies gaps, suggests fixes, and helps you keep documentation current.'
        ],
    },
  ];

  return (
    <section className="bg-[rgb(11,21,25)] py-32 text-white">
      <div className="container mx-auto mb-32">
        <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center">
          <h2 className="font-serif text-2xl text-white md:text-4xl">
            {t.features_pages['Your knowledge base, maintained automatically']}
          </h2>
          <p className="text-sm text-white/80 md:text-base">
            {
              t.features_pages[
                'Stop guessing what documentation to write. Agent 5 shows you exactly where your knowledge gaps are.'
              ]
            }
          </p>
        </div>
        <div className="mb-32 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature) => (
            <div
              key={feature.id}
              className="rounded-xl border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-white/20 md:p-7"
            >
              <h3 className="font-serif text-lg text-white md:text-xl">
                {feature.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-white/70">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function KnowledgeGapPage() {
  const { t, locale } = useTranslation();
  const isAr = locale === 'ar';

  return (
    <>
      <HelpdeskHero
        title={
          <>
            {t.features_pages['Garbage in, garbage out.']}
            <br />
            {t.features_pages['Fix the garbage.']}
          </>
        }
        subtitle={
          t.features_pages[
            'Your AI is only as good as your knowledge base. Knowledge Gap Detection finds bad documentation by tracking what causes Agent 5 to escalate to humans. Fix the gaps that matter most.'
          ]
        }
      />

      <Logos />

      <BenefitsSection
        title={t.features_pages['Knowledge Gap Detection']}
        subtitle={
          t.features_pages['Maintain your knowledge base automatically']
        }
        learnMoreLink="#faq"
        accentColor="emerald"
        backgroundColor="black"
        benefits={[
          {
            title: t.features_pages['Identify Bad Documentation'],
            description:
              t.features_pages[
                'Agent 5 tracks which knowledge sources cause handoffs to humans. Bad docs reveal themselves through escalation patterns.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl:
              '/product/knowledge-gap-detection/identify-bad-documentation.mp4',
            imagePosition: 'left',
          },
          {
            title: t.features_pages['Multi-Channel Gap Detection'],
            description:
              t.features_pages[
                "Detect gaps across Voice, Chat, Email, WhatsApp, Social, and Slack. Know what's missing everywhere."
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'right',
          },
          {
            title: t.features_pages['AI-Suggested Content'],
            description:
              t.features_pages[
                'Agent 5 suggests what to add to your knowledge base. Drafts documentation based on successful human agent responses.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl:
              '/product/knowledge-gap-detection/ai-suggested-content.mp4',
            imagePosition: 'left',
          },
        ]}
      />

      {/* Authentic Message Section */}
      <section className="overflow-hidden py-32">
        <div className="container w-full leading-relaxed">
          <h2 className="text-muted-foreground/40 mt-10 text-center font-serif text-3xl md:text-4xl">
            {isAr ? (
              <>
                <LinkPreview
                  url="https://en.wikipedia.org/wiki/Knowledge_base"
                  className="px-1"
                >
                  التوثيق
                </LinkPreview>{' '}
                السيّئ يقتل أداء الـ AI. وأغلب الفرق ما تعرف أي مستندات معطوبة
                إلا بعد فوات الأوان.
                <br />
                <br />
                Knowledge Gap Detection يراقب Agent 5 وهو يشتغل. ولمّا يحوّل
                المحادثة للبشر، يتعلّم النظام أي معرفة فشلت. الفجوات تتجمّع{' '}
                <LinkPreview
                  url="https://en.wikipedia.org/wiki/Cluster_analysis"
                  className="px-1"
                >
                  تلقائياً
                </LinkPreview>
                ، وتترتّب حسب التأثير، وتُعرض مع حلول يقترحها الـ AI.
                <br />
                <br />
                قاعدة معرفتك تصلّح نفسها. وأنت بس توافق على التغييرات.
              </>
            ) : (
              <>
                Bad{' '}
                <LinkPreview
                  url="https://en.wikipedia.org/wiki/Knowledge_base"
                  className="px-1"
                >
                  documentation
                </LinkPreview>{' '}
                kills AI performance. Most teams don&apos;t know which docs are
                broken until it&apos;s too late.
                <br />
                <br />
                Knowledge Gap Detection watches Agent 5 work. When it escalates
                to humans, the system learns which knowledge failed. Gaps are
                automatically{' '}
                <LinkPreview
                  url="https://en.wikipedia.org/wiki/Cluster_analysis"
                  className="px-1"
                >
                  clustered
                </LinkPreview>
                , prioritized by impact, and presented with AI-suggested fixes.
                <br />
                <br />
                Your knowledge base fixes itself. You just approve the changes.
              </>
            )}
          </h2>
        </div>
      </section>

      {/* Capabilities Section - Dark */}
      <GapDetectionCapabilities />

      {/* FAQ Section */}
      <FaqSection
        title={t.helpdesk['Frequently asked questions']}
        subtitle={
          t.features_pages[
            'Find answers to common questions about Knowledge Gap Detection.'
          ]
        }
        items={[
          {
            question: t.features_pages['How does it detect knowledge gaps?'],
            answer:
              t.features_pages[
                'By tracking which knowledge sources cause Agent 5 to escalate to humans. High escalation rate = bad documentation.'
              ],
          },
          {
            question: t.features_pages['What channels are analyzed?'],
            answer:
              t.features_pages[
                'All of them. Voice, Chat, Email, WhatsApp, Social, and Slack. Gaps are detected across every customer touchpoint.'
              ],
          },
          {
            question: t.features_pages['How are gaps prioritized?'],
            answer:
              t.features_pages[
                'By impact. Gaps that cause the most handoffs appear first. Fix what matters most to customers.'
              ],
          },
          {
            question: t.features_pages['Does it suggest how to fix gaps?'],
            answer:
              t.features_pages[
                'Yes. Agent 5 analyzes successful human responses and suggests documentation to add. Drafts content based on what actually worked.'
              ],
          },
          {
            question: t.features_pages['How often are gaps detected?'],
            answer:
              t.features_pages[
                'Continuously. Every interaction is analyzed in real-time. Gaps are identified as they emerge.'
              ],
          },
          {
            question:
              t.features_pages[
                'Can this really maintain my knowledge base on autopilot?'
              ],
            answer:
              t.features_pages[
                'Not fully automated—you still approve changes. But it tells you exactly what to fix, why it matters, and suggests the content. Knowledge maintenance becomes easy.'
              ],
          },
        ]}
      />

      <Testimonials />
    </>
  );
}
